/**
 * Text Fallback KWS Implementation
 * 
 * Uses the existing TriggerDetector for wake word detection when Porcupine is not available.
 * Processes partial speech recognition results to detect wake phrases.
 */

import { KWSAdapter } from './KWSAdapter.js';
import { TriggerDetector } from '../TriggerDetector.js';

export class TextFallbackKWS extends KWSAdapter {
    constructor() {
        super();
        this.triggerDetector = null;
        this.isInitialized = false;
        this.isListening = false;
        this.onDetectCallback = null;
        this.lastScore = 0;
        this.wakePhrase = 'hey chef';
        this.speechEngine = null;
    }

    async init(options) {
        try {
            this.wakePhrase = options.wakePhrase || 'hey chef';
            
            // Initialize TriggerDetector with exact wake phrase matching
            this.triggerDetector = new TriggerDetector({
                wakeSequences: [[this.wakePhrase.split(' ')[0], this.wakePhrase.split(' ')[1]]], // e.g., [['hey', 'chef']]
                requireConsecutiveMatches: true,
                consecutiveMatchCount: 1,
                maxGapTokens: 0,
                windowMs: 4000,
                negationWindow: 4,
                refractoryPeriod: 3000
            });

            // Set up trigger detection callback
            this.triggerDetector.setOnTriggerDetected((event) => {
                this.lastScore = event.score || 1.0;
                if (this.onDetectCallback) {
                    this.onDetectCallback({
                        phrase: this.wakePhrase,
                        score: this.lastScore,
                        timestamp: Date.now()
                    });
                }
            });

            this.isInitialized = true;
            console.log('🎤 TextFallbackKWS: Initialized with wake phrase:', this.wakePhrase);
            return true;
        } catch (error) {
            console.error('🎤 TextFallbackKWS: Initialization failed:', error);
            return false;
        }
    }

    async start() {
        if (!this.isInitialized || !this.triggerDetector) {
            console.error('🎤 TextFallbackKWS: Not initialized');
            return false;
        }

        try {
            // Use BrowserSTT solely for wake-word partials; ignore finals
            const { BrowserSTT } = await import('../BrowserSTT.js');
            
            this.speechEngine = new BrowserSTT({
                language: 'en-US',
                maxAlternatives: 1
            });

            // Configure for continuous partial results
            if (this.speechEngine.recognition) {
                this.speechEngine.recognition.continuous = true;
                this.speechEngine.recognition.interimResults = true;
            }

            // Feed partials into trigger detector
            this.speechEngine.setOnPartial((result) => {
                if (this.triggerDetector && result && result.transcript) {
                    const normalized = this.normalizeTranscript(result.transcript);
                    this.triggerDetector.pushPartial(normalized);
                }
            });

            // Ignore finals to avoid premature command capture
            this.speechEngine.setOnFinal(() => {
                // no-op: KWS does not emit finals
            });

            await this.speechEngine.start();
            this.isListening = true;
            console.log('🎤 TextFallbackKWS: Started BrowserSTT for wake phrase detection');
            return true;
        } catch (error) {
            console.error('🎤 TextFallbackKWS: Failed to start:', error);
            return false;
        }
    }

    async stop() {
        try {
            if (this.speechEngine) {
                await this.speechEngine.stop();
            }
            this.isListening = false;
            console.log('🎤 TextFallbackKWS: Stopped listening');
            return true;
        } catch (error) {
            console.error('🎤 TextFallbackKWS: Failed to stop:', error);
            return false;
        }
    }

    async destroy() {
        try {
            await this.stop();
            
            if (this.triggerDetector) {
                this.triggerDetector.reset();
                this.triggerDetector = null;
            }

            if (this.speechEngine) {
                await this.speechEngine.destroy();
                this.speechEngine = null;
            }

            this.isInitialized = false;
            this.onDetectCallback = null;
            
            console.log('🎤 TextFallbackKWS: Destroyed');
            return true;
        } catch (error) {
            console.error('🎤 TextFallbackKWS: Failed to destroy:', error);
            return false;
        }
    }

    async setWakePhrase(phrase) {
        this.wakePhrase = phrase;
        
        if (this.triggerDetector) {
            // Reinitialize trigger detector with new phrase
            const words = phrase.split(' ');
            if (words.length >= 2) {
                this.triggerDetector.reset();
                // Note: TriggerDetector constructor options can't be changed after creation
                // We would need to recreate it, but for now we'll keep the original phrase
                console.log('🎤 TextFallbackKWS: Wake phrase updated to:', phrase);
            }
        }
        
        return true;
    }

    isReady() {
        return this.isInitialized && this.triggerDetector !== null;
    }

    getLastScore() {
        return this.lastScore;
    }

    onDetect(callback) {
        this.onDetectCallback = callback;
    }

    removeDetectCallback() {
        this.onDetectCallback = null;
    }

    /**
     * Normalize transcript for trigger detection
     */
    /**
     * Feed partial transcript to trigger detector
     * This is called by SmartSpeechEngine when it receives partial results
     */
    feedPartialTranscript(transcript) {
        if (this.triggerDetector && this.isListening && transcript) {
            const normalized = this.normalizeTranscript(transcript);
            this.triggerDetector.pushPartial(normalized);
        }
    }

    normalizeTranscript(transcript) {
        return transcript
            .toLowerCase()
            .replace(/[^\w\s]/g, ' ') // Remove punctuation
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim();
    }
}

export default TextFallbackKWS;
